import React, { JSX, useEffect, useState, useContext, useRef } from "react";
import { useRouter } from "next/router";

import { GlobalContext } from "@/contexts/GlobalContext";
import { AuthContext } from "@/contexts/AuthContext";

import useFetchVehicle from "@/hooks/useFetchVehicle";
import useFetch from "@/hooks/useFetchData";

import ScrollButton from "@/components/ScrollButton";
import CartButton from "@/components/CartButton.component";
import ErrorComponent from "@/components/Error.component";
import Breadcrumbs from "@/components/Breadcrumbs.component";
import Notification from "@/components/Notification.component";

import SiteHead from "@/layouts/Head";
import Layout from "@/layouts/Layout";

import { 
    Params, 
    Vehicle, 
    Prices, 
    Stock, 
    VoloObject, 
    Product, 
    ListImages
} from "@/interfaces/SharedInterfaces";
import { FetchOptions } from "@/types/HTTP.types";
import { VehicleIndex } from "@/types/Vehicles.types";

import styles from "@/styles/product.module.scss";

const ProductDetails = (props: { 
    errorCode: number | boolean, 
    details: Product, 
    rv: VehicleIndex[], 
    stock: Stock[], 
    prices: Prices 
}) => {

    if (props.errorCode) {
        return <ErrorComponent statusCode={props.errorCode} />
    }

    const { globalState: { apiUrl, vhInfo } } = useContext(GlobalContext);
    const { authState: { isAdminAuth } } = useContext(AuthContext);
    const router = useRouter();
    const { query, query: { ktype, make, model, genartname, genart_nr: genartnr, sku } }: { query: Partial<Params> } = router;
    const { fetchVehicle } = useFetchVehicle();
    const { useFetchData } = useFetch();

    const partsPath: string = `/car-parts/${make}/${model}/${ktype}`;
    const genartPath: string = partsPath + `/${genartname}/${genartnr}`;
    const intBrands: string[] = ["TRUCKTEC", "TTC", "VEMO", "VIEROL", "SACHS", "LEMFORDER"];
    const criteriasMounted = useRef<boolean>(false);
    const skusMounted = useRef<boolean>(false);
    const imagesMounted = useRef<boolean>(false);

    const [prod, setProd] = useState<Product | null>(null);
    const [images, setImages] = useState<ListImages[] | null>(null);
    const [productStock, setProductStock] = useState<Stock[] | null>(null);
    const [productPrices, setProductPrices] = useState<Prices | null>(null);
    const [vCrts, setVCrts] = useState<VoloObject[] | null>(null);
    const [voloCrts, setVoloCrts] = useState<VoloObject[] | null>(null);
    const [relVehicle, setRelVehicle] = useState<VehicleIndex[] | null>(null);
    const [listsku, setListskus] = useState<{ ref_sku: string; list_sku: string; }[] | null>(null);

    const [genartName, setGenartName] = useState<string | null>(null);
    const [vehicle, setVehicle] = useState<Vehicle | null>(null);

    const [parseSap, setParseSap] = useState<boolean>(false);
    const [fetchedCrts, setFetchedCrts] = useState<boolean>(false);
    const [fetchedImages, setFetchedImages] = useState<boolean>(false);

    useEffect(() => {
        if (query.ktype && !vhInfo) {
            fetchVehicle(query.ktype);
        }
    }, [query, vhInfo]);

    useEffect(() => {
        setVCrts(null);
        setFetchedCrts(false);
        criteriasMounted.current = false;
    }, [sku]);

    useEffect(() => {
        if (props.details) {
            setProd(props.details);
        }

        if (props.rv) {
            setRelVehicle(props.rv);
        }

        if (props.stock) {
            setProductStock(props.stock);
        }

        if (props.prices) {
            setProductPrices(props.prices);
        }
    }, [props]);

    useEffect(() => {
        if (genartname) {
            setGenartName(genartname.split("-").map(string => string.replace(/^\w/, (c) => c.toUpperCase())).join(" "));
        }

        if (make) {
            setVehicle({...vehicle, make: make.replace(/^\w/, (c) => c.toUpperCase()), model: model!.replace(/^\w/, (c) => c.toUpperCase())});
        }
    }, [make]);

    useEffect(() => {
        if (productStock) {
            setParseSap(true);
        }
    }, [productStock]);

    useEffect(() => {
        skusMounted.current = false;

        if (isAdminAuth && prod) {
            fetchStockNumber([prod.ref_sku]);
        }

        return () => {
            skusMounted.current = true;
        }
    }, [isAdminAuth, prod]);

    useEffect(() => {
        criteriasMounted.current = false;

        if (!vCrts && !fetchedCrts && sku) {
            fetchVoloCriteria();
            setFetchedCrts(true);
        }

        return () => {
            criteriasMounted.current = true;
        };
    }, [vCrts, fetchedCrts, sku]);

    useEffect(() => {
        imagesMounted.current = false;

        if (!images && !fetchedCrts && sku) {
            fetchImages([sku]);
            setFetchedImages(true);
        }

        return () => {
            imagesMounted.current = true;
        };
    }, [images, fetchedImages, sku]);

    useEffect(() => {
        if (vCrts) {
            const tempCrts: VoloObject[] = [];
            vCrts.forEach(crt => {
                if ( crt.value?.match(/ebay/gi) || crt.name?.match(/choicepivot/gi) || !crt.value ) {
                } else {
                    tempCrts.push(crt);
                }
            });
            setVoloCrts(tempCrts);
        }
    }, [vCrts]);

    /*
     * --------------------------------------------------
     * API Calls 
     * --------------------------------------------------
     * */

    const fetchStockNumber = async (skus: string[]) => {
        const url: string = `${apiUrl}/admin/listskus`;
        const options: FetchOptions = {
            method: "POST",
            body: JSON.stringify({ cskus: skus })
        };

        try {
            const result = await useFetchData<{ref_sku: string; list_sku: string}[]>(url, options);

            if (!result.success) {
                return false;
            }

            setListskus(result.data);
        } catch (error) {
            console.error(error);
        }
    };

    const fetchVoloCriteria = async () => {
        const url: string = apiUrl + `/products/criteria?partno=${sku}`;

        try {
            const result = await useFetchData<VoloObject[]>(url);

            if (!result.success) {
                return false;
            }

            setVCrts(result.data);
        } catch (error) {
            console.error(error);
        }
    };

    const fetchImages = async (skus: string[]) => {
        const url: string = `${apiUrl}/products/images`;
        const options: FetchOptions = {
            method: "POST",
            body: JSON.stringify({ cskus: skus, type: "ref" })
        };

        try {
            const result = await useFetchData<ListImages[]>(url, options);
            
            if (!result.success) {
                return false;
            }

            if (!imagesMounted.current) {
                setImages(result.data);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const productPrice = (prices: Prices | null): number => {

        switch (true) {
            case !prices:
                return -1;
            case prices && prices!.master === null:
                return parseFloat(prices!.list.value!);
            case prices && prices!.list === null:
                return parseFloat(prices!.master.value!);        
            case prices && parseFloat(prices!.list.value!) > 0 && parseFloat(prices!.master.value!) === 0:
                return parseFloat(prices!.list.value!);
            case prices && parseFloat(prices!.list.value!) === 0 && parseFloat(prices!.master.value!) > 0:
                return parseFloat(prices!.master.value!);
            case prices && parseFloat(prices!.list.value!) > 0 && parseFloat(prices!.master.value!) > 0:
                return parseFloat(prices!.list.value!);
            default:
                return 0;
        }
    };

    const stockLocation = (stock: Stock[], brand: string): JSX.Element | null => {
        let node: JSX.Element | null = null;
        let prodStock: Stock[] = stock;

        if (prodStock.length > 0) {
            for (let i = 0; i < prodStock.length; i++) {
                let loc = prodStock[i];

                if (!loc) {
                    continue;
                }
                
                if (loc.id === 7 || loc.id === 42 || loc.id === 29) {
                    node = <span className={styles["stock-in"]} >In Stock</span>;
                    break;
                } else if (loc.id !== 7 && loc.id !== 42 && loc.id !== 29 && loc.pickable) {
                    node = <span className={styles["stock-in"]} >1 day</span>;
                    break;
                } else if (loc.id !== 7 && loc.id !== 42 && loc.id !== 29 && !loc.pickable && intBrands.includes(brand.toUpperCase())) {
                    node = <span className={styles["stock-in"]} >4-6 days</span>;
                    break;
                } else {
                    node = <span className={styles["stock-in"]} >2-3 days</span>;
                    break;
                }
            }
        } else {
            node = <span className={styles["stock-out"]} >Not In Stock</span>;
        }

        return node;
    };

    const hasStock = (stock: Stock[] | null): boolean => {
        if (!stock) return false;

        let prodStock: Stock[] = stock;

        if (prodStock.length === 0) {
            return false;
        }

        return true;
    };

    const productImages = (sku: string) => {
        if (!images) return null;

        for (const img of images) {
            const ref = img.ref_sku;

            if (ref !== sku) {
                continue;
            }

            return img.images ? img.images[0].src : null;
        }
    };

    const splitVoloCriterias = (crt: VoloObject[]): VoloObject[] => {
        const vCriterias: VoloObject[] = [];

        crt?.forEach(crit => {
            if (crit.value) {
                const [_, ...end] = crit.name!.split(":");
                const crt: object = {
                    name: end.join("").trim(),
                    value: crit.value
                }
                vCriterias.push(crt);
            }
        });

        return vCriterias;
    };

    const resetVehicle = () => {
        localStorage.removeItem("__vhi");
        localStorage.removeItem("__ktype");
    };

    const brandImage = (brand: string) => brand.toUpperCase().replace(/-|\s&\s|\s/g, "_");

    const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
        event.currentTarget.onerror = null;
        return event.currentTarget.src = `${process.env.NEXT_PUBLIC_CDN_URI}/static/images/links/no-image.png`;
    };

    const priceClass = `price${!hasStock(productStock) ? "--nis" : ""}`;

    const prodPrice = productPrice(productPrices!) === 0 ? "N/A" : String.fromCharCode(163) + productPrice(productPrices!).toFixed(2);

    const cartButton = (sku: string, stock: Stock[] | null, prices: Prices | null): JSX.Element => {
        if (hasStock(stock) && productPrice(prices) > 0) {
            return <CartButton partno={sku} enabled={true} stocked={true} />;
        }

        if (!hasStock(stock) && productPrice(prices) > 0) {
            return <CartButton partno={sku} enabled={true} stocked={false} />;
        }

        return <CartButton partno={sku} enabled={true} stocked={false} />;
    };

    const prodCard = prod && (
        <div className={styles["prod-card"]}>
            <div className={styles["header"]}>
                <div className={styles["header-left"]}>
                    <div className={styles["brand-image"]}>
                        <img
                            src={`${process.env.NEXT_PUBLIC_CDN_URI}/static/logos/brands/${brandImage(prod.brand)}.png`}
                            alt={prod.brand.toLowerCase().replace(/-|\s&\s|\s/g, "_")}
                        />
                        {/* {prod?.brand && <img src={`/assets/logos/brands/${prod?.brand.toUpperCase().replace(/-|\s&\s|\s/g, "_")}.png`} alt={prod.brand.toLowerCase()} />} */}
                    </div>
                </div>

                <div className={styles["header-right"]}>
                    <h1 className={styles["title"]}>{prod?.title}</h1>
                    <span className={styles["ref"]}>Ref No: {prod?.ref_sku}</span>
                </div>
            </div>

            <div className={styles["info"]}>
                <div className={styles["visuals"]}>
                    <div className={styles["images"]}>
                        <img 
                            src={productImages(prod.ref_sku) || `${process.env.NEXT_PUBLIC_CDN_URI}/static/images/links/no-image.png`}
                            onError={handleImageError}
                            alt={prod?.title.toLowerCase()} 
                        />
                    </div>
                </div>

                <div className={styles["details"]}>
                    <div className={styles["details-stock"]}>
                        <div className={styles["stock-avail"]}>
                            <span className={styles["stock-avail-text"]} >Availability:</span>
                            {!parseSap ? <div className={styles["stock-loader-box"]} ><div className={styles["stock-loader"]} /></div> : stockLocation(productStock!, prod?.brand!)}
                        </div>
                        {isAdminAuth && listsku && <div className={styles["sku"]}>
                                    <strong>StockNumber: </strong>
                                    <p>{listsku[0].list_sku}</p>
                                </div>}
                    </div>

                    <div className={styles["details-price"]}>
                        {parseSap && <span className={styles[priceClass]}>{prodPrice}</span>}
                        {cartButton(prod.ref_sku, productStock, productPrices)}
                        {isAdminAuth && prod?.mpn !== "" && 
                            <a 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                href={`http://www.ebaystores.co.uk/Doctor-Car-Parts/_i.html?_nkw=${prod?.mpn}&submit=Search&_sid=90962233`}
                            >
                                <img 
                                    src={`${process.env.NEXT_PUBLIC_CDN_URI}/static/images/links/ebay-logo.png`} 
                                    className={styles["ebay-logo"]} 
                                />
                            </a>
                        }
                    </div>

                </div>
            </div>
        </div>
    );

    const prodCriterias = (
        <ul className={styles["criteria-list"]}>
            {prod?.crts && prod.crts.map((crt, idx) => (
                <li key={idx} className={styles["crt-row"]}>
                    <div className={styles["crt-keys"]}>
                        <p>{crt.crt_key}</p>
                    </div>
                    <div className={styles["crt-values"]}>
                        <p>{crt.crt_value}</p>
                    </div>
                </li>
            ))}
        </ul>
    );

    const voloCriterias = (
        <ul className={styles["criteria-list"]}>
            {parseSap && splitVoloCriterias(voloCrts!).map((crt, idx) => {
                    return (
                        <li key={idx} className={styles["crt-row"]}>
                            <div className={styles["crt-keys"]}>
                                <p>{crt.name}</p>
                            </div>
                            <div className={styles["crt-values"]}>
                                <p dangerouslySetInnerHTML={{__html: crt.value!}} />
                            </div>
                        </li>
                    );
                }
            )}
        </ul>
    );

    const relVehicleLink = (vh: VehicleIndex): JSX.Element => {
        const make = vh.MAKE.replace(/\s/g, "-").toLowerCase();
        const model = vh.MODEL1?.replace(/\./g, "_").replace(/-|,\s|,/g, "-").replace(/\s/g, "-").toLowerCase();
        const kcode = vh.KCODE_B;

        const aMake = vh.MAKE;
        const aModel = vh.MODEL1;
        const aModel2 = vh.MODEL2 ? vh.MODEL2 : "";
        const aSub = vh.SUBMODEL ? "(" + vh.SUBMODEL + ")" : "";
        const aBody = vh.BODY ? vh.BODY : "";
        const aEng = vh.ENG1 ? vh.ENG1 : "";
        const aFuel = vh.FUEL;
        const aPower = `${vh.BHP}BHP/${vh.KW}KW`;
        const aYear = `${vh.FROMYR}-${vh.TOYR}`;
        const aBlock = `${vh.VALVES && vh.CYL ? parseInt(vh.VALVES) * parseInt(vh.CYL) : ""}V`;
        const aCode = vh.ENGCODE1 ? vh.ENGCODE1 : "";

        return (
            query.ktype === kcode ?
            <span className={styles["curr-vehicle"]}>{`${aMake} ${aModel} ${aModel2} ${aSub} - ${aBody} - ${aEng} ${aFuel} - ${aPower}- [${aYear}] - ${aBlock} ${aCode}`}</span> :
            <a 
                href={`/car-parts/${make}/${model}/${kcode}`} 
                onClick={resetVehicle} 
            >{`${aMake} ${aModel} ${aModel2} ${aSub} - ${aBody} - ${aEng} ${aFuel} - ${aPower}- [${aYear}] - ${aBlock} ${aCode}`}</a>
        );
    };

    const relVehiclesList = (
        <ul className={styles["vehicles-list"]}>
            {relVehicle && relVehicle.map((vh, idx) => (
                <li key={idx} className={styles["vehicle-row"]} >
                    {relVehicleLink(vh)}
                </li>
            ))}
        </ul>
    );

    return (
        <React.Fragment>
            <SiteHead 
                title={prod?.title || ""} 
                rs={true} 
                prod={prod} 
                desc={prod?.title} 
                keywords={prod?.title.toLowerCase().split(" ").join(", ")} 
                genartnr={genartnr} 
                genartname={genartName!} 
                price={parseSap ? productPrice(productPrices!).toFixed(2) : "0.00"} 
                stock={parseSap && hasStock(productStock!) ? "InStock" : "OutOfStock"} 
                bc={genartname ? "prod" : "prime"} 
            />

            <Notification timeout={3000} />

            <Layout>
                {!router.pathname.match(/products/) && <Breadcrumbs
                    path_1={partsPath}
                    path_2={`${genartPath}?qt=std`}
                    name_1={`${vhInfo?.MAKE} ${vhInfo?.MODEL1}`}
                    name_2={genartName}
                    current={prod?.title}
                />}

                <div className={styles["container"]}>
                    {prodCard}
                    <div className={styles["criteria"]}>
                        <h2 className={styles["criteria-title"]}>Technical Details</h2>
                        {(prod && prod!.crts.length > 0) || (voloCrts && voloCrts.length > 0) ? <div className={styles["criteria-split-box"]}>
                            {prod && prod!.crts.length > 0 && prodCriterias}
                            {voloCrts && voloCrts.length > 0 && voloCriterias}
                        </div> : <p 
                            className={styles["criteria-none-text"]} 
                            style={{
                                textAlign: "center", 
                                marginBottom: "0", 
                                fontFamily: "Roboto", 
                                fontSize: "0.9rem", 
                                color: "gray"
                            }} 
                        >No Technical Details Found</p>}
                    </div>

                    <div className={styles["related-vehicles"]}>
                        <h2 className={styles["vehicles-title"]}>Compatible Models</h2>
                        {relVehicle && relVehicle.length > 0 && relVehiclesList}
                    </div>
                </div>
            </Layout>
            <ScrollButton scrollLength={500} />
        </React.Fragment>
    );
};

export default ProductDetails;
